// src/components/ArchivedChatsModal.tsx
import { logger } from '../utils/logger';
// Modal to view and restore archived chats and groups

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonText,
} from '@ionic/react';
import { close, arrowUndo } from 'ionicons/icons';
import { getArchivedChats, getArchivedGroups, unarchiveChat, unarchiveGroup } from '../services/chatPreferencesService';
import { supabase } from '../services/supabase';
import { AvatarPreview } from './avatar/AvatarPreview';

interface ArchivedChatsModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onUnarchive?: () => void; // Callback to refresh parent
}

interface ArchivedChat {
  id: string;
  otherUser: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
    avatar: any;
  };
}

interface ArchivedGroup {
  id: string;
  name: string;
  member_count: number;
}

export const ArchivedChatsModal: React.FC<ArchivedChatsModalProps> = ({
  isOpen,
  onDismiss,
  onUnarchive,
}) => {
  const [segment, setSegment] = useState<'chats' | 'groups'>('chats');
  const [archivedChats, setArchivedChats] = useState<ArchivedChat[]>([]);
  const [archivedGroups, setArchivedGroups] = useState<ArchivedGroup[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadArchivedItems();
    }
  }, [isOpen]);

  const loadArchivedItems = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get archived chat IDs
      const archivedChatIds = await getArchivedChats();

      // Fetch full chat details for archived chats
      if (archivedChatIds.length > 0) {
        const { data: matches } = await supabase
          .from('matches')
          .select(`
            id,
            user1_id,
            user2_id,
            user1:profiles!matches_user1_id_fkey (id, username, display_name, avatar_url, meeple_avatar),
            user2:profiles!matches_user2_id_fkey (id, username, display_name, avatar_url, meeple_avatar)
          `)
          .in('id', archivedChatIds);

        const formattedChats: ArchivedChat[] = (matches || []).map((match: any) => {
          const isUser1 = match.user1_id === user.id;
          const otherUser = isUser1 ? match.user2 : match.user1;
          return {
            id: match.id,
            otherUser: {
              id: otherUser?.id || '',
              username: otherUser?.username || '',
              display_name: otherUser?.display_name || '',
              avatar_url: otherUser?.avatar_url || null,
              avatar: otherUser?.meeple_avatar || null,
            },
          };
        });
        setArchivedChats(formattedChats);
      } else {
        setArchivedChats([]);
      }

      // Get archived group IDs
      const archivedGroupIds = await getArchivedGroups();

      // Fetch full group details
      if (archivedGroupIds.length > 0) {
        const { data: groups } = await supabase
          .from('groups')
          .select('id, name, member_count:group_members(count)')
          .in('id', archivedGroupIds);

        const formattedGroups: ArchivedGroup[] = (groups || []).map(group => ({
          id: group.id,
          name: group.name,
          member_count: group.member_count?.[0]?.count || 0,
        }));
        setArchivedGroups(formattedGroups);
      } else {
        setArchivedGroups([]);
      }
    } catch (error) {
      logger.error('Error loading archived items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchiveChat = async (chatId: string) => {
    const result = await unarchiveChat(chatId);
    if (result.success) {
      setArchivedChats(prev => prev.filter(c => c.id !== chatId));
      onUnarchive?.();
    }
  };

  const handleUnarchiveGroup = async (groupId: string) => {
    const result = await unarchiveGroup(groupId);
    if (result.success) {
      setArchivedGroups(prev => prev.filter(g => g.id !== groupId));
      onUnarchive?.();
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onDismiss} aria-label="Close archived chats">
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
          <IonTitle>Archived</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={segment} onIonChange={e => setSegment(e.detail.value as 'chats' | 'groups')}>
            <IonSegmentButton value="chats">
              <IonLabel>Chats ({archivedChats.length})</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="groups">
              <IonLabel>Groups ({archivedGroups.length})</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IonSpinner />
          </div>
        ) : segment === 'chats' ? (
          archivedChats.length === 0 ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '20px' }}
              role="status"
              aria-label="No archived chats"
            >
              <IonText color="medium">
                <p>No archived chats</p>
              </IonText>
            </div>
          ) : (
            <IonList>
              {archivedChats.map(chat => (
                <IonItem key={chat.id}>
                  <IonAvatar slot="start">
                    {chat.otherUser.avatar_url ? (
                      <img src={chat.otherUser.avatar_url} alt={chat.otherUser.display_name || chat.otherUser.username} />
                    ) : chat.otherUser.avatar ? (
                      <AvatarPreview
                        avatarData={chat.otherUser.avatar}
                        size={40}
                        userId={chat.otherUser.id}
                      />
                    ) : (
                      <AvatarPreview size={40} userId={chat.otherUser.id} />
                    )}
                  </IonAvatar>
                  <IonLabel>
                    <h2>{chat.otherUser.display_name || chat.otherUser.username}</h2>
                  </IonLabel>
                  <IonButton
                    slot="end"
                    fill="outline"
                    size="small"
                    onClick={() => handleUnarchiveChat(chat.id)}
                  >
                    <IonIcon icon={arrowUndo} slot="start" />
                    Restore
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          )
        ) : (
          archivedGroups.length === 0 ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '20px' }}
              role="status"
              aria-label="No archived groups"
            >
              <IonText color="medium">
                <p>No archived groups</p>
              </IonText>
            </div>
          ) : (
            <IonList>
              {archivedGroups.map(group => (
                <IonItem key={group.id}>
                  <IonAvatar slot="start">
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'var(--ion-color-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    >
                      {group.name.charAt(0).toUpperCase()}
                    </div>
                  </IonAvatar>
                  <IonLabel>
                    <h2>{group.name}</h2>
                    <p>{group.member_count} members</p>
                  </IonLabel>
                  <IonButton
                    slot="end"
                    fill="outline"
                    size="small"
                    onClick={() => handleUnarchiveGroup(group.id)}
                  >
                    <IonIcon icon={arrowUndo} slot="start" />
                    Restore
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          )
        )}
      </IonContent>
    </IonModal>
  );
};
